/**
 * Travel time handling.
 *
 * Rules (brief §6): keep raw strings, distinguish date-only values from instants, resolve a
 * wall-clock time in an explicit IANA zone, detect DST gaps (nonexistent times) and overlaps
 * (ambiguous times), and never fall back to the server's or browser's zone silently.
 * Uses Intl only (IANA tzdata shipped with the runtime), so it runs in browsers and Workers.
 */

export type LocalDateTime = string; // 'YYYY-MM-DDTHH:mm'
export type LocalDate = string; // 'YYYY-MM-DD'

export type ZoneResolution =
  | { status: 'resolved'; utc: string }
  | { status: 'ambiguous'; candidates: [string, string] } // [earlier instant, later instant]
  | { status: 'nonexistent'; shiftedUtc: string } // wall time skipped by a DST gap
  | { status: 'no_zone' }
  | { status: 'invalid' };

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const dtfCache = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = dtfCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    dtfCache.set(tz, f);
  }
  return f;
}

export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    formatter(tz);
    return true;
  } catch {
    return false;
  }
}

export function isLocalDateTime(v: string): v is LocalDateTime {
  return LOCAL_RE.test(v);
}
export function isLocalDate(v: string): v is LocalDate {
  return DATE_RE.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`));
}

/** Wall-clock parts of an instant in a zone. */
export function partsInZone(utcMs: number, tz: string) {
  const parts = formatter(tz).formatToParts(new Date(utcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute'), second: get('second') };
}

/** UTC offset of a zone at an instant, in minutes (e.g. +60 for CET). */
export function offsetMinutes(utcMs: number, tz: string): number {
  const p = partsInZone(utcMs, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(utcMs / 1000) * 1000) / 60000);
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

export function utcToLocal(utc: string | number | Date, tz: string): LocalDateTime {
  const ms = typeof utc === 'number' ? utc : new Date(utc).getTime();
  const p = partsInZone(ms, tz);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

export function localDateOf(utc: string | number | Date, tz: string): LocalDate {
  return utcToLocal(utc, tz).slice(0, 10);
}

/**
 * Resolve a wall-clock time in a zone to a UTC instant, reporting DST gaps and overlaps
 * instead of guessing.
 */
export function wallTimeToUtc(local: string, tz: string | null | undefined): ZoneResolution {
  const m = LOCAL_RE.exec(local);
  if (!m) return { status: 'invalid' };
  if (!tz) return { status: 'no_zone' };
  if (!isValidTimeZone(tz)) return { status: 'invalid' };
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return { status: 'invalid' };
  const naive = Date.UTC(y, mo - 1, d, h, mi);
  if (new Date(naive).getUTCDate() !== d) return { status: 'invalid' }; // e.g. 31 April

  // The true offset is one of the offsets in force shortly before or after this moment.
  const offsets = new Set([offsetMinutes(naive - 86_400_000, tz), offsetMinutes(naive, tz), offsetMinutes(naive + 86_400_000, tz)]);
  const valid = new Set<number>();
  for (const o of offsets) {
    const candidate = naive - o * 60_000;
    if (utcToLocal(candidate, tz) === local) valid.add(candidate);
  }
  const sorted = [...valid].sort((a, b) => a - b);
  if (sorted.length === 1) return { status: 'resolved', utc: new Date(sorted[0]!).toISOString() };
  if (sorted.length >= 2) {
    return { status: 'ambiguous', candidates: [new Date(sorted[0]!).toISOString(), new Date(sorted[sorted.length - 1]!).toISOString()] };
  }
  // In a gap: interpret with the offset before the transition, which lands after the gap.
  const before = offsetMinutes(naive - 86_400_000, tz);
  return { status: 'nonexistent', shiftedUtc: new Date(naive - before * 60_000).toISOString() };
}

/** Short zone label for display, e.g. "GMT", "CEST", or "GMT+2" depending on runtime data. */
export function zoneAbbreviation(utc: string | number | Date, tz: string, locale = 'en-GB'): string {
  const parts = new Intl.DateTimeFormat(locale, { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date(utc));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
}

/** Join the parts of one value ("Sat 24 Oct", "10:30 AM", "€ 250") with no-break spaces so it never splits across lines. */
export function keepTogether(s: string): string {
  return s.replace(/[ \u202f]/g, '\u00a0');
}

export function formatTime(utc: string, tz: string, locale = 'en-GB'): string {
  return keepTogether(new Intl.DateTimeFormat(locale, { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(utc)));
}

export function formatDate(date: LocalDate, locale = 'en-GB', opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }): string {
  // Date-only values are calendar dates, not instants: format at UTC noon to avoid shifts.
  return keepTogether(new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'UTC' }).format(new Date(`${date}T12:00:00Z`)));
}

export function addDays(date: LocalDate, days: number): LocalDate {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(from: LocalDate, to: LocalDate): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export function minutesBetween(aUtc: string, bUtc: string): number {
  return Math.round((new Date(bUtc).getTime() - new Date(aUtc).getTime()) / 60_000);
}

export function formatDuration(minutes: number): string {
  const sign = minutes < 0 ? '−' : '';
  const m = Math.abs(minutes);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${sign}${r} min`;
  return r === 0 ? `${sign}${h} h` : `${sign}${h} h ${r} min`;
}

/** The zone the device reports; used only as a visible, overridable default. */
export function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

export function supportedTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : ['UTC', 'Europe/London', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Paris', 'America/New_York', 'America/Chicago', 'America/Los_Angeles'];
}
